import axios from "axios";
import { etat as etatConnexion, signalerHorsLigne, signalerEnLigne } from "@/horsLigne/etat";
import { cleCopie, garderCopie, lireCopie, effacerDonneesLocales } from "@/horsLigne/reference";

//  Base URL 
let baseURL = process.env.VUE_APP_API_BASE_URL;

//   Si la variable .env n'existe pas
if (!baseURL) {
  const hostname = window.location.hostname;

  // Réseau local
  if (
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    hostname === "localhost"
  ) {
    baseURL = "http://127.0.0.1:8000";
  } 
  // PRODUCTION
  else {
    baseURL = "https://api-clinique.mata-groupe.com";
  }
}

// Adresse de l'API (sans /api/v1), pour les appels hors instance principale
export const API_BASE = baseURL;

//  Instance Axios propre
const apiClient = axios.create({
  baseURL: `${baseURL}/api/v1`,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json"
  }
});

//  Intercepteur JWT
apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// ===================================
// MESSAGE NON BLOQUANT (toast)
// ===================================
// Affiche un message en bas à droite pendant quelques secondes.
// Utilisé quand le serveur refuse une action (droits insuffisants).
export function notifier(message, type = 'danger') {
  try {
    let zone = document.getElementById('toast-zone-api');
    if (!zone) {
      zone = document.createElement('div');
      zone.id = 'toast-zone-api';
      zone.style.cssText = 'position:fixed;right:16px;bottom:16px;z-index:2000;max-width:360px;';
      document.body.appendChild(zone);
    }
    // Même message déjà affiché : on ne l'empile pas (appels répétés en arrière-plan)
    if ([...zone.children].some(t => t.textContent === message)) return;
    const toast = document.createElement('div');
    toast.className = `alert alert-${type} shadow-sm mb-2`;
    toast.setAttribute('role', 'alert');
    toast.textContent = message;
    zone.appendChild(toast);
    setTimeout(() => toast.remove(), 6000);
  } catch (e) {
    alert(message);
  }
}

// Message lisible renvoyé par l'API (Symfony / API Platform)
export function messageErreur(error, defaut = "Une erreur est survenue.") {
  const d = error?.response?.data;
  return d?.message || d?.detail || d?.['hydra:description'] || d?.error || defaut;
}

// Responsable de l'abonnement (d'après le jeton de connexion)
function peutRenouveler() {
  try {
    const t = localStorage.getItem("token");
    const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return (p.roles || []).includes('ROLE_SUPER_ADMIN')
      || (p.permissions || []).map(x => String(x).toUpperCase()).includes('ABONNEMENT_GERER');
  } catch (e) {
    return false;
  }
}

// ===================================
// INTERCEPTEUR REPONSE
// ===================================
apiClient.interceptors.response.use(

  response => {
    signalerEnLigne();
    // Dernière copie de la salle d'attente et de l'agenda, pour les coupures
    const cle = cleCopie(response.config);
    if (cle) garderCopie(cle, response.data);
    return response;
  },

  async error => {

    // Pas de réponse du serveur : coupure d'internet ou serveur arrêté
    if (!error.response && !axios.isCancel(error)) {
      signalerHorsLigne();
      const cle = cleCopie(error.config);
      if (cle) {
        const copie = await lireCopie(cle);
        if (copie) {
          etatConnexion.copieAffichee = copie.le;
          return { data: copie.data, status: 200, config: error.config, headers: {}, horsLigne: true };
        }
      }
      if ((error.config?.method || 'get').toLowerCase() !== 'get') {
        notifier("Pas de connexion au serveur : rien n'a été enregistré. Pour accueillir les patients, utilisez « Accueil hors connexion ».", 'warning');
      }
      error.horsLigne = true;
      return Promise.reject(error);
    }

    // Action refusée par le serveur : les droits sont vérifiés côté API,
    // même si un bouton est visible par erreur dans l'interface.
    if (error.response?.status === 403) {
      notifier(messageErreur(error, "Vous n'avez pas la permission d'effectuer cette action."));
    }

    // Encaissement refusé faute de caisse ouverte : on propose de l'ouvrir
    if (error.response?.status === 409 && /caisse n'est pas ouverte/i.test(messageErreur(error, ''))) {
      window.dispatchEvent(new CustomEvent('caisse:ouverture-requise'))
    }

    // Clinique suspendue ou abonnement expiré
    if (error.response?.status === 402) {
      const message = messageErreur(error, "L'abonnement de votre clinique a expiré.");
      // Le responsable de l'abonnement reste connecté pour pouvoir le renouveler
      if (peutRenouveler()) {
        if (window.location.pathname !== "/mon-abonnement") {
          window.location.href = "/mon-abonnement";
        }
        return Promise.reject(error);
      }
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");
      await effacerDonneesLocales();
      if (window.location.pathname !== "/login") {
        // Message transmis par le navigateur, jamais par l'adresse : un lien piégé
        // ne peut pas afficher un faux avertissement sur la page de connexion
        try { sessionStorage.setItem("keneya.messageConnexion", message); } catch (e) { /* sans stockage */ }
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");
      await effacerDonneesLocales();

      if (window.location.pathname !== "/login") {

        alert("Votre session a expiré.");

        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

/** Paramètres de requête sans les valeurs vides. */
function sansVides(params = {}) {
  const r = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') r[k] = v;
  });
  return r;
}

export default {
  /**
   * Liste paginée et filtrée par le serveur : tous les paramètres sont transmis
   * (page, itemsperpage, recherche, filtres propres à l'écran).
   */
  liste(resource, params = {}) {
    return apiClient.get(`/${resource}`, { params: sansVides(params) });
  },

  /**
   * Lecture d'une ressource. Sans page demandée (listes de choix : types,
   * moyens de paiement…), on demande la taille maximale (100) ; pour une liste
   * à l'écran, utiliser liste() avec la pagination du serveur.
   */
  getItems(resource, { orderBy, itemsperpage, itemsPerPage, sortOrder, page, search, ...filtres } = {}) {
    const params = sansVides({ orderBy, sortOrder, page, search, ...filtres, itemsperpage: itemsperpage || itemsPerPage || (page ? undefined : 100) });
    return apiClient.get(`/${resource}`, { params });
  },

  createItem(resource, data, isMultipart = false) {
    return apiClient.post(`/${resource}`, data, {
      headers: isMultipart
        ? { "Content-Type": "multipart/form-data" }
        : { "Content-Type": "application/json" }
    });
  },

  updatePassword(data) {
    return apiClient.put(`/users/update_password`, data);
  },

  updateItem(resource, id, data, isMultipart = false) {
    const url = id ? `/${resource}/${id}` : `/${resource}`;

    if (isMultipart) {
      return apiClient.post(url, data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    }

    return apiClient.put(url, data);
  },
  getMe() {
  return apiClient.get(`/me`);
},

  deleteItem(resource, id) {
    return apiClient.delete(`/${resource}/${id}`);
  },
  custom(url, method = "PATCH", data = null) {
  return apiClient({
    url: `/${url}`,
    method,
    data
  });
},

  /*patchItem(resource, id, data) {
    return apiClient.put(`/${resource}/${id}`, data);
  },*/
  patchItem(resource, id, data) {
  return apiClient.patch(`/${resource}/${id}`, data);
},
patchMergeItem(resource, id, data) {
    return apiClient.patch(
      `/${resource}/${id}`,
      data,
      {
        headers: {
          'Content-Type': 'application/merge-patch+json'
        }
      }
    );
  },
  getItem(resource, id) {
    return apiClient.get(`/${resource}/${id}`);
  },

  post(resource, data) {
    return apiClient.post(`/${resource}`, data);
  },

  // Télécharge un fichier protégé (PDF, CSV) avec le jeton de connexion
  async telecharger(resource, nomFichier, ouvrir = false) {
    const res = await apiClient.get(`/${resource}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    if (ouvrir) {
      window.open(url, '_blank');
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = nomFichier;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
};
